import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { msalInstance, loginRequest } from '../msalConfig';
import SessaoPill from '../components/SessaoPill';
import SessaoExpiradaModal from '../components/SessaoExpiradaModal';
import {
  EVENTO_SESSAO_EXPIRADA,
  contaDoUsuario,
  expiracaoDoToken,
  guardarRetorno,
  instanteDeRenovacao,
  registrarLeitorDeToken,
  registrarRenovador,
  renovarSessao,
  retentarApos,
} from '../lib/sessao';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// O /microsoft-login valida o token na Microsoft (Graph /me) antes de responder:
// sem teto, uma renovação pendurada travaria a pílula em "Renovando…".
const TIMEOUT_LOGIN_MS = 20 * 1000;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const lerToken = () => {
  try { return localStorage.getItem('auth_token'); } catch { return null; }
};
const lerUsuario = () => {
  try { return JSON.parse(localStorage.getItem('auth_user') || 'null'); } catch { return null; }
};

// O MSAL só consegue renovar em silêncio enquanto a credencial Microsoft vale;
// quando exige interação (login_required, consent_required, MFA vencido) só um
// redirect resolve — e redirect no meio do trabalho é o que este arquivo evita.
const exigeInteracao = (error) =>
  error?.name === 'InteractionRequiredAuthError' ||
  ['interaction_required', 'login_required', 'consent_required'].includes(error?.errorCode);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  // Contagem da pílula + estado da renovação (ver lib/sessao.js).
  const [expiraEm, setExpiraEm] = useState(() => expiracaoDoToken(lerToken()));
  const [renovando, setRenovando] = useState(false);
  const [falhouRenovar, setFalhouRenovar] = useState(false);
  const [tentativasFalhas, setTentativasFalhas] = useState(0);
  const [sessionExpired, setSessionExpired] = useState(false);
  const initRodou = useRef(false);

  const guardarSessao = useCallback((accessToken, userData) => {
    setToken(accessToken);
    setUser(userData);
    setExpiraEm(expiracaoDoToken(accessToken));
    setFalhouRenovar(false);
    setTentativasFalhas(0);
    setSessionExpired(false);
    localStorage.setItem('auth_token', accessToken);
    localStorage.setItem('auth_user', JSON.stringify(userData));
  }, []);

  const clearAuth = useCallback(() => {
    setUser(null);
    setToken(null);
    setExpiraEm(null);
    setFalhouRenovar(false);
    setTentativasFalhas(0);
    setSessionExpired(false);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
  }, []);

  // Troca a credencial Microsoft pelo JWT do painel. Sem `existingAccessToken`
  // (conta do cache), pega via acquireTokenSilent — que NUNCA redireciona
  // daqui: se exigir interação, devolve falha e quem chamou decide (o aviso
  // oferece "Entrar de novo"; a tela de login faz o redirect).
  const exchangeTokenWithBackend = useCallback(async (msalAccount, existingAccessToken) => {
    let accessToken = existingAccessToken;
    if (!accessToken) {
      try {
        const tokenResponse = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account: msalAccount,
        });
        accessToken = tokenResponse.accessToken;
      } catch (error) {
        console.warn('[AUTH] acquireTokenSilent falhou:', error?.errorCode || error?.message || error);
        return {
          success: false,
          precisaEntrar: exigeInteracao(error),
          message: exigeInteracao(error)
            ? 'A credencial Microsoft expirou — é preciso entrar de novo.'
            : 'Não foi possível obter a credencial Microsoft. Tente novamente.',
        };
      }
    }

    let response;
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_LOGIN_MS);
      try {
        response = await fetch(`${API_URL}/api/auth/microsoft-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ access_token: accessToken }),
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      console.error('[AUTH] Falha de rede no microsoft-login:', error);
      return {
        success: false,
        message: error?.name === 'AbortError'
          ? 'O servidor demorou para responder. Tente novamente.'
          : 'Erro ao conectar com o servidor. Tente novamente.',
      };
    }

    let data = {};
    try { data = await response.json(); } catch { data = {}; }
    if (!response.ok || !data.access_token) {
      return { success: false, status: response.status, message: data.detail || 'Erro ao autenticar com Microsoft' };
    }
    guardarSessao(data.access_token, data.user);
    return { success: true, user: data.user };
  }, [guardarSessao]);

  // ---- Reentra pela conta Microsoft que já está no cache do navegador ----
  // Conta escolhida pelo e-mail do usuário logado (duas contas @caremfo no
  // mesmo navegador não podem trocar de identidade sem prompt). Sem redirect.
  // Usado pela renovação automática, pelo "Continuar conectado" e pela
  // abertura fria com o token vencido.
  const reentrarPeloMsal = useCallback(async () => {
    const emailLogado = (lerUsuario() || {}).email || null;
    let contas = [];
    try { contas = msalInstance.getAllAccounts(); } catch { contas = []; }
    const conta = contaDoUsuario(contas, emailLogado);
    if (!conta) return false;
    const r = await exchangeTokenWithBackend(conta);
    return !!r.success;
  }, [exchangeTokenWithBackend]);

  // A ponte de lib/sessao.js é o ponto ÚNICO de renovação: authedFetch (401),
  // timer, vigia, pílula e aviso passam por ela, deduplicados.
  useEffect(() => {
    registrarRenovador(reentrarPeloMsal);
    registrarLeitorDeToken(lerToken);
    return () => {
      registrarRenovador(null);
      registrarLeitorDeToken(null);
    };
  }, [reentrarPeloMsal]);

  // Renova sem sair da tela (com estado para a pílula e o aviso).
  const renovar = useCallback(async () => {
    setRenovando(true);
    try {
      const ok = await renovarSessao();
      if (ok) {
        setFalhouRenovar(false);
        setTentativasFalhas(0);
        setSessionExpired(false);
      } else {
        setFalhouRenovar(true);
        setTentativasFalhas((n) => n + 1);
      }
      return ok;
    } finally {
      setRenovando(false);
    }
  }, []);

  // ---- Renovação automática ----
  // A 10 min do fim (com jitter) ou, se a última falhou com o token ainda
  // vivo, na retentativa com backoff (60 s, 120 s, 240 s… até 8 min). Token
  // já morto é caso do vigia abaixo — sem esta guarda, uma falha com token
  // vencido reagendaria "daqui a 1 s" para sempre.
  useEffect(() => {
    if (!user || expiraEm === null || sessionExpired) return undefined;
    const agora = Date.now();
    if (expiraEm <= agora) return undefined;
    const quando = falhouRenovar
      ? Math.min(agora + retentarApos(tentativasFalhas), expiraEm)
      : instanteDeRenovacao(expiraEm, agora);
    const id = setTimeout(() => { renovar(); }, Math.max(1000, quando - agora));
    return () => clearTimeout(id);
  }, [user, expiraEm, falhouRenovar, tentativasFalhas, sessionExpired, renovar]);

  // ---- Vigia ----
  // O token morreu sem renovar (aba dormiu com o computador fechado, timer
  // atrasado, retentativas esgotadas): tenta uma vez em silêncio; se não der,
  // o aviso assume — em cima da tela, que continua montada.
  useEffect(() => {
    if (!user || expiraEm === null || sessionExpired) return undefined;
    let cancelado = false;
    const checar = () => {
      if (expiraEm > Date.now()) return;
      renovar().then((ok) => { if (!ok && !cancelado) setSessionExpired(true); });
    };
    checar();
    const id = setInterval(checar, 30 * 1000);
    return () => { cancelado = true; clearInterval(id); };
  }, [user, expiraEm, sessionExpired, renovar]);

  // Token trocado por OUTRA aba: adota e reagenda a partir dele.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'auth_token' || !e.newValue) return;
      setToken(e.newValue);
      setExpiraEm(expiracaoDoToken(e.newValue));
      setFalhouRenovar(false);
      setTentativasFalhas(0);
      setSessionExpired(false);
      const salvo = lerUsuario();
      if (salvo) setUser(salvo);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // authedFetch: 401 que sobreviveu à renovação com o token vencido. A tela
  // FICA montada; o aviso cobre e oferece "Continuar conectado".
  useEffect(() => {
    const onExpirada = () => {
      // Depois de um "Sair" voluntário não há token: uma request em voo que
      // chegou tarde não pode reabrir o aviso na tela de login.
      if (!lerToken()) return;
      setSessionExpired(true);
    };
    window.addEventListener(EVENTO_SESSAO_EXPIRADA, onExpirada);
    return () => window.removeEventListener(EVENTO_SESSAO_EXPIRADA, onExpirada);
  }, []);

  useEffect(() => {
    if (initRodou.current) return;
    initRodou.current = true;

    const init = async () => {
      // 1. Sessão salva: confere no servidor
      const savedToken = lerToken();
      const savedUser = lerUsuario();

      if (savedToken && savedUser) {
        setToken(savedToken);
        setUser(savedUser);
        setExpiraEm(expiracaoDoToken(savedToken));
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${savedToken}` }
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            localStorage.setItem('auth_user', JSON.stringify(userData));
          } else if (response.status === 401) {
            // Token vencido (a manhã seguinte). A conta Microsoft
            // continua no cache do navegador — mesmo caminho do "Continuar
            // conectado". Antes, limpava e jogava no login sem tentar nada.
            const renovou = await renovarSessao();
            if (!renovou) clearAuth();
          } else if (response.status === 403 || response.status === 404) {
            // Conta bloqueada ou removida: não há o que renovar.
            clearAuth();
          }
          // 5xx / backend subindo: mantém a sessão salva; um 401 depois renova.
        } catch (error) {
          console.error('[AUTH] Erro ao verificar token:', error);
        }
        setLoading(false);
        return;
      }

      // 2. Processar redirect do Microsoft (se voltou com #code=)
      try {
        const redirectResponse = await msalInstance.handleRedirectPromise();
        if (redirectResponse) {
          await exchangeTokenWithBackend(redirectResponse.account, redirectResponse.accessToken);
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('[AUTH] Erro ao processar redirect:', error);
      }

      // 3. Verificar se já existe conta no MSAL (login anterior)
      let accounts = [];
      try { accounts = msalInstance.getAllAccounts(); } catch { accounts = []; }
      if (accounts.length > 0) {
        await exchangeTokenWithBackend(accounts[0]);
      }

      setLoading(false);
    };

    init();
  }, [exchangeTokenWithBackend, clearAuth]);

  const loginWithMicrosoft = async () => {
    try {
      // Usa redirect em vez de popup — mais confiável
      await msalInstance.loginRedirect(loginRequest);
      // Não retorna nada — o navegador vai redirecionar para o Microsoft
      // e quando voltar, o useEffect acima processa o resultado
    } catch (error) {
      console.error('[AUTH] Erro ao iniciar login:', error);
      console.error('[AUTH] Error code:', error.errorCode, '| Message:', error.message);
      if (error.errorCode === 'user_cancelled') {
        return { success: false, message: 'Login cancelado' };
      }
      return { success: false, message: 'Erro ao conectar com Microsoft. Tente novamente.' };
    }
  };

  const logout = useCallback(() => {
    clearAuth();
    try {
      if (msalInstance.getAllAccounts().length > 0) {
        msalInstance.clearCache();
      }
    } catch (error) {
      console.warn('[AUTH] clearCache falhou:', error);
    }
  }, [clearAuth]);

  // "Entrar de novo" no aviso: guarda a página (com query) e limpa a sessão —
  // ProtectedRoute/ConsultorRoute levam ao /login, e o Login volta para cá.
  const entrarDeNovo = useCallback(() => {
    guardarRetorno(window.location.pathname + window.location.search);
    clearAuth();
  }, [clearAuth]);

  const getAuthHeaders = () => {
    if (token) {
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
    }
    return { 'Content-Type': 'application/json' };
  };

  const value = {
    user,
    token,
    loading,
    loginWithMicrosoft,
    logout,
    getAuthHeaders,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    // Pílula de contagem + aviso de expirada (ver lib/sessao.js).
    sessao: {
      expiraEm,
      expirada: sessionExpired,
      renovando,
      falhouRenovar,
      renovar,
      entrarDeNovo,
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {user && <SessaoPill sessao={value.sessao} />}
      {user && sessionExpired && <SessaoExpiradaModal sessao={value.sessao} />}
    </AuthContext.Provider>
  );
};
